import { makeAutoObservable, runInAction } from "mobx";
import { INTELLIGENCE_IMPORTANT_MIN, INTELLIGENCE_PAGE_SIZE } from "./Config";
import { IntelligenceFeedApi } from "./IntelligenceFeedApi";
import type {
  IntelligenceFeedItem,
  IntelligenceFeedListKind,
} from "./IntelligenceFeedType";

export class IntelligenceFeedModel {
  items: IntelligenceFeedItem[] = [];
  total = 0;
  loading = false;
  loadingMore = false;
  scope: IntelligenceFeedListKind = "all";
  newsType: number | null = null;
  importantOnly = false;
  draftKeyword = "";
  appliedKeyword = "";
  loadError = "";

  private nextPage = 2;
  private requestVersion = 0;

  constructor(
    readonly spaceId: string,
    readonly spaceProductId: string
  ) {
    makeAutoObservable(
      this,
      {
        spaceId: false,
        spaceProductId: false,
      },
      { autoBind: true }
    );
  }

  get hasMore(): boolean {
    return this.items.length < this.total;
  }

  get hasActiveFilter(): boolean {
    return Boolean(this.appliedKeyword || this.newsType != null || this.importantOnly);
  }

  setDraftKeyword(value: string): void {
    this.draftKeyword = value;
  }

  async applyKeyword(): Promise<void> {
    this.appliedKeyword = this.draftKeyword.trim();
    await this.loadFirst();
  }

  /** 清除搜索框：去掉关键词并重新拉列表 */
  async clearKeyword(): Promise<void> {
    this.draftKeyword = "";
    this.appliedKeyword = "";
    await this.loadFirst();
  }

  async clearFilters(): Promise<void> {
    this.draftKeyword = "";
    this.appliedKeyword = "";
    this.newsType = null;
    this.importantOnly = false;
    await this.loadFirst();
  }

  async selectScope(scope: IntelligenceFeedListKind): Promise<void> {
    if (scope === this.scope) return;
    this.scope = scope;
    await this.loadFirst();
  }

  async selectNewsType(type: number): Promise<void> {
    this.newsType = this.newsType === type ? null : type;
    await this.loadFirst();
  }

  async toggleImportantOnly(): Promise<void> {
    this.importantOnly = !this.importantOnly;
    await this.loadFirst();
  }

  async viewLatest(): Promise<void> {
    this.scope = "all";
    this.newsType = null;
    this.importantOnly = false;
    this.draftKeyword = "";
    this.appliedKeyword = "";
    await this.loadFirst();
  }

  async loadFirst(): Promise<void> {
    const version = ++this.requestVersion;
    this.loading = true;
    this.loadError = "";
    try {
      const res = await IntelligenceFeedApi.list(this.scope, this.buildQuery(1));
      if (version !== this.requestVersion) return;
      runInAction(() => {
        if (typeof res === "string") {
          this.items = [];
          this.total = 0;
          this.loadError = "情报加载失败，请稍后重试";
          return;
        }
        this.items = res.list;
        this.total = res.total;
        this.nextPage = 2;
      });
    } finally {
      if (version === this.requestVersion) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  async loadMore(): Promise<void> {
    if (this.loading || this.loadingMore || !this.hasMore) return;
    const version = this.requestVersion;
    const page = this.nextPage;
    this.loadingMore = true;
    try {
      const res = await IntelligenceFeedApi.list(this.scope, this.buildQuery(page));
      if (version !== this.requestVersion || typeof res === "string") return;
      runInAction(() => {
        const existing = new Set(this.items.map((item) => item.id));
        this.items = [
          ...this.items,
          ...res.list.filter((item) => !existing.has(item.id)),
        ];
        this.total = res.total;
        this.nextPage = page + 1;
      });
    } finally {
      runInAction(() => {
        this.loadingMore = false;
      });
    }
  }

  /** 轮询合并首页：更新/前置新条，保留已加载分页，避免整表替换闪烁。 */
  async syncVisible(): Promise<void> {
    const res = await IntelligenceFeedApi.list(this.scope, this.buildQuery(1));
    if (typeof res === "string") return;
    runInAction(() => {
      const firstIds = new Set(res.list.map((item) => item.id));
      const rest = this.items.filter((item) => !firstIds.has(item.id));
      this.items = [...res.list, ...rest];
      this.total = res.total;
    });
  }

  async toggleLike(item: IntelligenceFeedItem): Promise<void> {
    const res = await IntelligenceFeedApi.toggleLike({
      spaceId: this.spaceId,
      spaceProductId: this.spaceProductId,
      intelligenceId: item.id,
    });
    if (typeof res === "string") return;
    runInAction(() => {
      if (this.scope === "liked" && !res.liked) {
        this.removeItem(item.id);
        return;
      }
      this.patchEngagement(item.id, res.liked, res.collected);
    });
  }

  async toggleCollect(item: IntelligenceFeedItem): Promise<void> {
    const res = await IntelligenceFeedApi.toggleCollect({
      spaceId: this.spaceId,
      spaceProductId: this.spaceProductId,
      intelligenceId: item.id,
    });
    if (typeof res === "string") return;
    runInAction(() => {
      if (this.scope === "collected" && !res.collected) {
        this.removeItem(item.id);
        return;
      }
      this.patchEngagement(item.id, res.liked, res.collected);
    });
  }

  private buildQuery(pageNum: number) {
    return {
      spaceId: this.spaceId,
      spaceProductId: this.spaceProductId,
      pageNum,
      pageSize: INTELLIGENCE_PAGE_SIZE,
      keyword: this.appliedKeyword || undefined,
      type: this.newsType ?? undefined,
      minImportance: this.importantOnly ? INTELLIGENCE_IMPORTANT_MIN : undefined,
    };
  }

  private removeItem(id: string): void {
    this.items = this.items.filter((item) => item.id !== id);
    this.total = Math.max(0, this.total - 1);
  }

  private patchEngagement(id: string, liked: boolean, collected: boolean): void {
    this.items = this.items.map((item) => {
      if (item.id !== id) return item;
      const likeDelta = Number(liked) - Number(Boolean(item.likedByMe));
      const collectDelta =
        Number(collected) - Number(Boolean(item.collectedByMe));
      return {
        ...item,
        likedByMe: liked,
        collectedByMe: collected,
        likeCount: Math.max(0, item.likeCount + likeDelta),
        collectCount: Math.max(0, item.collectCount + collectDelta),
      };
    });
  }
}
